/** Hash router (ca butonul Back/Forward al browserului să meargă). */

/** Secțiunile din pagina de detaliu site - segment opțional în hash, ca un refresh să rămână pe tabul curent.
 *  Hash-urile fără segment (sau cu un segment necunoscut) cad pe „overview" (tabul „Dashboard"), implicitul. */
export type SiteTab = "overview" | "insights" | "conversations" | "widget" | "settings";
const SITE_TABS: SiteTab[] = ["overview", "insights", "conversations", "widget", "settings"];
/** Taburile vechi, păstrate ca alias: „stats" (volum) a devenit „overview", iar
 *  „progress" (interpretare) a devenit „insights" - linkurile și bookmark-urile
 *  deja trimise trebuie să aterizeze pe echivalentul lor, nu pe implicit. */
const LEGACY_SITE_TABS: Record<string, SiteTab> = { stats: "overview", progress: "insights" };

/** Tipurile de conținut din Content Hub - trei intrări de nav, un singur UI. */
export type ContentKind = "news" | "research" | "blog";
const CONTENT_KINDS: ContentKind[] = ["news", "research", "blog"];

export type Route =
  | { name: "dashboard" | "plan" | "referral" | "activity" | "console" | "codes" | "tickets" | "settings" }
  | { name: "site"; appId: string; tab: SiteTab }
  // Lista unei secțiuni de conținut (id null), editorul unui articol existent
  // (id numeric), sau editorul unui articol NESALVAT ÎNCĂ (id "new" - vezi
  // ContentEditorView: nu se creează nimic pe server până la primul Save/Generate,
  // ca „New item" să nu murdărească baza cu drafturi „untitled" abandonate).
  | { name: "content"; kind: ContentKind; id: number | "new" | null };

export function parseHash(): Route {
  // partea de query (#/plan?checkout=success - redirectul din Stripe Checkout)
  // nu e segment de rută; fără tăierea ei, „plan?checkout=…" n-ar mai fi „plan"
  const parts = location.hash.replace(/^#\/?/, "").split("?")[0].split("/").filter(Boolean);
  // Backward compat: /sites/{id} și /sites → /dashboard/{id} și /dashboard
  if ((parts[0] === "sites" || parts[0] === "dashboard") && parts[1]) {
    const seg = parts[2] ?? "";
    const tab: SiteTab = SITE_TABS.includes(seg as SiteTab) ? (seg as SiteTab) : (LEGACY_SITE_TABS[seg] ?? "overview");
    return { name: "site", appId: decodeURIComponent(parts[1]), tab };
  }
  if (parts[0] === "sites" || parts[0] === "dashboard") return { name: "dashboard" };
  // #/plan = pagina de plan (și URL-ul de retur al Stripe Checkout,
  // #/plan?checkout=success|cancel). Când cumpărarea e închisă, rămâne
  // recunoscută și trimisă la dashboard, nu scoasă de tot: linkuri vechi,
  // bookmark-uri și retururi de Checkout ar ajunge altfel la o rută necunoscută.
  if (parts[0] === "plan") return BILLING_SELF_SERVE ? { name: "plan" } : { name: "dashboard" };
  if (parts[0] === "referral") return { name: "referral" };
  if (parts[0] === "activity") return { name: "activity" };
  if (parts[0] === "console") return { name: "console" };
  if (parts[0] === "codes") return { name: "codes" };
  if (parts[0] === "tickets") return { name: "tickets" };
  // #/content/{kind} (listă), #/content/{kind}/new (editor nesalvat) sau
  // #/content/{kind}/{id} (editor pe articol existent).
  if (parts[0] === "content" && CONTENT_KINDS.includes(parts[1] as ContentKind)) {
    const kind = parts[1] as ContentKind;
    if (!parts[2]) return { name: "content", kind, id: null };
    if (parts[2] === "new") return { name: "content", kind, id: "new" };
    if (/^\d+$/.test(parts[2])) return { name: "content", kind, id: Number(parts[2]) };
    return { name: "content", kind, id: null }; // segment necunoscut → cade pe listă
  }
  if (parts[0] === "settings") return { name: "settings" };
  return { name: "dashboard" };
}

/** Navighează schimbând hash-ul → intră în istoricul browserului (back merge). */
export function navigate(hash: string): void {
  if (location.hash === hash) return;
  location.hash = hash;
}

/** Tab-urile din AuthView (login/register/forgot) - link direct posibil cu #/login etc.
 *  Implicit login: cine ajunge pe app.askmywebsiteai.com/ nelogat vede întâi ecranul
 *  de autentificare, nu formularul de înregistrare. */
export type AuthMode = "login" | "register" | "forgot";

/**
 * Înregistrările publice: închise 7 aug - 24 oct 2026 (private beta), deschise
 * din 24 oct 2026. Oglindește `SIGNUPS_OPEN` din `backend/src/routes/admin.ts`,
 * care e poarta ADEVĂRATĂ — asta doar arată/ascunde tab-ul și trimite
 * `#/register` la login când e închis, ca nimeni să nu completeze un formular
 * pe care serverul îl refuză. Cele două se schimbă împreună; dacă diverg,
 * backendul câștigă (cu un 403).
 */
export const SIGNUPS_OPEN = true;

/**
 * Cumpărarea self-serve, oglindă a lui `BILLING_SELF_SERVE` din
 * `backend/src/routes/billing.ts` — acolo e poarta adevărată (403
 * `billing_closed` pe toate rutele), asta doar arată/ascunde ce depinde de ea:
 * pagina de plan, „Manage billing", bannerele de abonament. Deschisă din
 * 24 oct 2026 (docs/plan-relansare-abonamente.md, Pas B simplificat).
 */
export const BILLING_SELF_SERVE = true;

/**
 * @param emptyHashMode tab-ul când hash-ul nu spune nimic (link de pe landing,
 *   fără `#/…`). Implicit login; „register" pentru cine vine cu intenție de plan
 *   sau din demo și n-are încă o sesiune - un client nou nu trebuie primit cu
 *   „Welcome back". Un hash explicit (#/login, #/register) câștigă mereu.
 */
export function parseAuthMode(emptyHashMode: AuthMode = "login"): AuthMode {
  const parts = location.hash.replace(/^#\/?/, "").split("?")[0].split("/").filter(Boolean);
  if (parts.length === 0 && emptyHashMode === "register") return SIGNUPS_OPEN ? "register" : "login";
  if (parts[0] === "register") return SIGNUPS_OPEN ? "register" : "login";
  if (parts[0] === "forgot") return "forgot";
  return "login";
}
